import React from "react";
import {
  TextField,
  Button,
  Typography,
  Box,
  Paper,
  Alert,
  Link as MuiLink,
} from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useLoginMutation } from "api/endpoints/auth";
import { useAppDispatch } from "store/store-hooks";
import { setToken } from "store/reducers/auth/auth.reducer";
import { useNavigate } from "react-router-dom";
import { ROUTE_PATH } from "router/router-constant/router.constant";
import { Link } from "react-router-dom";

const validationSchema = Yup.object({
  phoneNumber: Yup.string()
    .matches(/^09\d{9}$/, "شماره تلفن باید با 09 شروع شده و 11 رقم باشد")
    .required("شماره تلفن الزامی است"),
  password: Yup.string().required("رمز عبور الزامی است"),
});

interface FormValues {
  phoneNumber: string;
  password: string;
}

export const AuthLoginSimpleComponent = () => {
  const [login, { isLoading, error }] = useLoginMutation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const handleSubmit = async (values: FormValues) => {
    try {
      const result = await login({
        payload: values,
      }).unwrap();

      // Store the token and user info
      dispatch(
        setToken({
          token: result.token,
          userId: result.user.id,
          user: result.user,
        })
      );

      // Redirect to main page and let RoleBasedRedirect handle the routing
      navigate("/");
    } catch (err) {
      console.error("Login failed:", err);
    }
  };

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      bgcolor="grey.100"
      p={{ xs: 1, sm: 2 }}
    >
      <Paper elevation={3} sx={{ 
        p: { xs: 3, sm: 4 }, 
        maxWidth: 400, 
        width: "100%",
        borderRadius: { xs: 2, sm: 3 }
      }}>
        <Box textAlign="center" mb={3}>
          <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: "1.5rem", sm: "2rem" } }}>
            ورود به Velora
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: "0.9rem", sm: "0.875rem" } }}>
            به پنل مدیریت اشتراک خود خوش آمدید
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2, fontSize: { xs: "0.9rem", sm: "0.875rem" } }}>
            {"data" in error && (error.data as any)?.message
              ? (error.data as any).message
              : "نام کاربری یا رمز عبور اشتباه است"}
          </Alert>
        )}

        <Formik
          initialValues={{
            phoneNumber: "",
            password: "",
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched, handleChange, handleBlur }) => (
            <Form>
              <TextField
                fullWidth
                name="phoneNumber"
                label="شماره تلفن"
                value={values.phoneNumber}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.phoneNumber && Boolean(errors.phoneNumber)}
                helperText={touched.phoneNumber && errors.phoneNumber}
                margin="normal"
                placeholder="09123456789"
                dir="ltr"
              />

              <TextField
                fullWidth
                name="password"
                label="رمز عبور (حداقل 4 رقم)"
                type="password"
                value={values.password}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.password && Boolean(errors.password)}
                helperText={
                  touched.password && errors.password
                    ? errors.password
                    : "رمز عبور خود را وارد کنید (حداقل 4 رقم)"
                }
                margin="normal"
                dir="ltr"
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={isLoading}
                sx={{ 
                  mt: 3, 
                  mb: 2,
                  minHeight: { xs: "48px", sm: "36px" },
                  fontSize: { xs: "1rem", sm: "0.875rem" }
                }}
              >
                {isLoading ? "در حال ورود..." : "ورود"}
              </Button>

              <Box textAlign="center">
                <Typography variant="body2" sx={{ fontSize: { xs: "0.9rem", sm: "0.875rem" } }}>
                  حساب کاربری ندارید؟{" "}
                  <MuiLink component={Link} to={ROUTE_PATH.auth.register.path}>
                    ثبت نام کنید
                  </MuiLink>
                </Typography>
              </Box>
            </Form>
          )}
        </Formik>
      </Paper>
    </Box>
  );
};
